import * as React from 'react';

import { faCamera } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';

import imagePlaceholder from 'src/assets/images/placeholder.webp';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselItemSubtitle,
    CarouselItemTitle,
    CarouselNext,
    CarouselPrevious,
} from 'src/registry/ui/Carousel';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { commonMessages } from 'src/utils/messages';
import { cn } from 'src/utils/shadcn';
import ImageCarouselInModal from 'src/features/visitor/components/carousel/ImageCarouselInModal';

export type ImageSlide = {
    image: string | null;
    title: string | undefined;
    subtitle: string | null;
};

type ImageCarouselProps = {
    imageSlides?: ImageSlide[];
    className?: string;
};

const ImageCarousel = ({ imageSlides, className }: ImageCarouselProps) => {
    const { formatMessage } = useIntl();
    const [selectedSlideImage, setSelectedSlideImage] = React.useState<ImageSlide | undefined>();
    const [currentIndex, setCurrentIndex] = React.useState<number>(0);

    const defaultImageSlides = [
        {
            image: imagePlaceholder,
            title: formatMessage(commonMessages.noImage),
            subtitle: '',
        },
    ];
    const hasAtLeastOneImage = !!imageSlides && imageSlides.length > 0;
    const hasAtLeastTwoImages = !!imageSlides && imageSlides.length > 1;
    const slidesToUse = hasAtLeastOneImage ? imageSlides : defaultImageSlides;

    return (
        <Carousel className={cn('flex w-full flex-col', className)}>
            <div className="relative">
                <Dialog>
                    <CarouselContent>
                        {slidesToUse?.map((slide, index) => (
                            <CarouselItem key={index}>
                                <DialogTrigger
                                    key={index}
                                    asChild
                                    onClick={() => {
                                        if (!hasAtLeastOneImage) return;
                                        setSelectedSlideImage(slide);
                                    }}
                                >
                                    <div className="h-full w-full overflow-hidden rounded-2xl">
                                        <img
                                            src={slide.image ?? imagePlaceholder}
                                            alt={slide.title}
                                            className={cn(
                                                'h-full w-full rounded-2xl object-cover',
                                                hasAtLeastOneImage && 'cursor-pointer',
                                            )}
                                        />
                                    </div>
                                </DialogTrigger>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <DialogContent className="flex size-[90%] h-auto max-w-3xl flex-col overflow-hidden">
                        <DialogHeader className="flex-none">
                            <div className="flex flex-row gap-2">
                                <SquareIcon icon={faCamera} />
                                <div>
                                    <DialogTitle>{selectedSlideImage?.title}</DialogTitle>
                                    <DialogDescription>{selectedSlideImage?.subtitle}</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>
                        <DialogSeparator />
                        <DialogBody className="flex items-center justify-center">
                            <ImageCarouselInModal
                                imageSlides={slidesToUse}
                                selectedSlide={selectedSlideImage}
                                setSelectedSlide={setSelectedSlideImage}
                            />
                        </DialogBody>
                    </DialogContent>
                </Dialog>
                <div
                    className={cn(
                        'absolute right-0 bottom-0 left-0 flex items-center justify-between rounded-br-2xl rounded-bl-2xl bg-black/50 p-2 text-white',
                        !hasAtLeastTwoImages && 'flex-row justify-center',
                    )}
                >
                    <div className={cn('relative flex items-center justify-center', !hasAtLeastTwoImages && 'hidden')}>
                        <CarouselPrevious
                            imageLength={slidesToUse?.length || 0}
                            className="left-2 cursor-pointer rounded-lg"
                            color="black"
                            onClick={() => setCurrentIndex(!currentIndex ? slidesToUse?.length - 1 : currentIndex - 1)}
                        />
                    </div>

                    <div className="flex flex-col items-center text-center">
                        <CarouselItemTitle className="text-sm font-medium">
                            {slidesToUse[currentIndex]?.title}
                        </CarouselItemTitle>
                        <CarouselItemSubtitle className="text-xs">
                            {slidesToUse[currentIndex]?.subtitle}
                        </CarouselItemSubtitle>
                    </div>

                    <div className={cn('relative flex items-center justify-center', !hasAtLeastTwoImages && 'hidden')}>
                        <CarouselNext
                            className="right-2 cursor-pointer rounded-lg"
                            color="black"
                            onClick={() =>
                                setCurrentIndex(slidesToUse?.length - 1 === currentIndex ? 0 : currentIndex + 1)
                            }
                        />
                    </div>
                </div>
            </div>
        </Carousel>
    );
};

export default ImageCarousel;
